import { useEffect, useRef, useState } from "react";
import Modal from "@mui/material/Modal";
import {
  deepCopy,
  formatLongDatetime,
  isBlank,
  isEmptyList,
  isNullOrUndefined,
  logAnalyticsEvent,
} from "../../utils";
import { ReactComponent as Cross } from "../../assets/cross.svg";
import { ReactComponent as Upload } from "../../assets/upload.svg";
import { ReactComponent as Clock } from "../../assets/clock.svg";

import { useTranslation } from "react-i18next";
import { ANALYTICS_EVENTS } from "../../constants";
import { useMainContext, useMainDispatchContext } from "../../MainContext";
import {
  getOperationEventMediaUpload,
  getTurnaroundDetails,
  saveOperationEvent,
} from "../../api";
import DateTimeInput from "../DateTimeInput";
import moment from "moment-timezone";
import ImageAsset from "../ImageAsset";
import { debugLog } from "../../logging";
import EXIF from "exif-js";
import { parseEXIFDate } from "../../dateUtils";
import { Fade, Tooltip } from "@mui/material";
import { tooltipLightStyles } from "../../styleUtils";

export default function TurnaroundModuleDocsModal(props) {
  const { onClose, onSaved, item, timezone, turnaroundUuid } = props;
  const { t } = useTranslation();
  const dispatch = useMainDispatchContext();
  const mainContext = useMainContext();

  const mNow = moment().tz(timezone).startOf("minute");
  const { operationEventMediaUpload } = mainContext;

  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [imageToUpdate, setImageToUpdate] = useState(null);
  const [previewImageData, setPreviewImageData] = useState(null);
  const [previewImageDataTimestamp, setPreviewImageDataTimestamp] =
    useState(null);
  const [editRequest, setEditRequest] = useState({
    uuid: item?.uuid,
    mediaUuid: item?.image?.uuid,
    notes: !isBlank(item?.notes) ? item.notes : "",
    timestamp: !isNullOrUndefined(item?.timestamp) ? item.timestamp : null,
  });
  const fileRef = useRef(null);

  useEffect(() => {
    if (!isNullOrUndefined(item)) {
      // Get media upload for image
      getOperationEventMediaUpload(dispatch, item.uuid);
    }
  }, [dispatch, item]);

  const handleClose = () => {
    setEditRequest(null);
    setHasChanges(false);
    setImageToUpdate(null);
    setPreviewImageData(null);
    if (!isNullOrUndefined(onClose)) {
      onClose();
    }
  };

  async function handleSave() {
    setIsProcessing(true);

    if (!isBlank(editRequest.notes)) {
      // Clear photo
      editRequest.mediaUuid = null;
    } else {
      // Clear notes
      editRequest.notes = null;
      // Upload images if needed
      if (!isNullOrUndefined(imageToUpdate)) {
        await handleUpload();
      }
    }

    const result = await saveOperationEvent(dispatch, editRequest);
    if (result) {
      dispatch({
        type: "setAlertMessage",
        alertMessage: t("saved_web", {
          name: item.name,
        }),
      });

      // refetch
      await getTurnaroundDetails(dispatch, { uuid: turnaroundUuid });
      if (!isNullOrUndefined(onSaved)) {
        onSaved();
      }
    }
    setIsProcessing(false);
    logAnalyticsEvent(
      dispatch,
      ANALYTICS_EVENTS.TURNAROUNDS_DETAILS_SAVE_OPERATION_EVENT
    );
    handleClose();
  }

  async function handleUpload() {
    try {
      const response = await fetch(operationEventMediaUpload.uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": imageToUpdate.fileType,
          "Cache-Control": "no-cache",
        },
        body: imageToUpdate.bytes,
      });
      await response.blob();
      if (response.ok) {
        debugLog(t("uploaded_image_message"));
      } else {
        debugLog(t("uploaded_image_error"));
      }
    } catch (error) {
      handleUploadComplete(t("uploaded_image_error"));
    }
  }

  function handleUploadComplete(message) {
    setIsUploading(false);
    dispatch({
      type: "setAlertMessage",
      alertMessage: message,
    });
  }

  async function handleSelectFile() {
    setIsUploading(true);
    if (isEmptyList(fileRef.current?.files)) return;
    let file = fileRef.current.files[0];

    // Read the file for uploading later
    let reader = new FileReader();
    const fileType = file.type;
    reader.onload = function () {
      let arrayBuffer = reader.result;
      let bytes = new Uint8Array(arrayBuffer);
      setImageToUpdate({
        bytes: bytes,
        fileType: fileType,
      });
      setEditRequest((prev) => {
        const updatedVal = deepCopy(prev);
        updatedVal.mediaUuid = operationEventMediaUpload.uuid;
        return updatedVal;
      });

      // Process the image metadata
      const tempImage = new Image();
      tempImage.onload = () => {
        EXIF.getData(tempImage, () => {
          const allMetaData = EXIF.getAllTags(tempImage);
          let imageTimestamp;
          if (allMetaData && allMetaData.DateTimeOriginal) {
            imageTimestamp = parseEXIFDate(allMetaData.DateTimeOriginal);
          } else if (allMetaData && allMetaData.DateTimeDigitized) {
            imageTimestamp = parseEXIFDate(allMetaData.DateTimeDigitized);
          }
          if (!isNullOrUndefined(imageTimestamp)) {
            setPreviewImageDataTimestamp(imageTimestamp);
            // By default, set as timestamp if it was empty
            if (isNullOrUndefined(editRequest?.timestamp))
              setEditRequest((prev) => {
                const updatedVal = deepCopy(prev);
                updatedVal.timestamp = imageTimestamp;
                return updatedVal;
              });
          }
        });
      };
      const blob = new Blob([arrayBuffer]);
      const imageSrc = URL.createObjectURL(blob);
      tempImage.src = imageSrc;
    };
    setPreviewImageDataTimestamp(null);
    reader.readAsArrayBuffer(file);

    // Read the file for previewing
    let readerForPreview = new FileReader();
    readerForPreview.onload = function () {
      const base64String = readerForPreview.result.split(",")[1];
      setPreviewImageData(base64String);
      setIsUploading(false);
      setHasChanges(true);
    };
    readerForPreview.readAsDataURL(file);
  }

  const hasImageToRender =
    !isNullOrUndefined(item?.image?.fullSize) ||
    !isNullOrUndefined(previewImageData);

  // Events must either have 1) photo w/ timestamp or 2) notes w/ timestamp
  const isValid =
    (!isBlank(editRequest.notes) &&
      !isNullOrUndefined(editRequest.timestamp)) ||
    (!isBlank(hasImageToRender) && !isNullOrUndefined(editRequest.timestamp));

  return (
    <div>
      <Modal open={!isNullOrUndefined(item)} onClose={handleClose}>
        <div
          className={`modal turnaround-module-docs-modal ${
            isBlank(editRequest.notes) && isBlank(editRequest.mediaUuid)
              ? "modal-medium"
              : "modal-50"
          }`}
        >
          <div>
            <div className="modal-header">
              <h2>
                {!isNullOrUndefined(item) && (
                  <>{`${t("edit")}: ${item.name}`}</>
                )}
              </h2>
              <div className="button-icon" onClick={handleClose}>
                <Cross />
              </div>
            </div>
          </div>
          <div className="modal-body">
            <div className="modal-container">
              <div className="turnaround-edit-modal-content">
                {!isNullOrUndefined(editRequest) && (
                  <>
                    <div className="turnaround-edit-modal-row wide">
                      <hr />
                    </div>

                    <div className={`turnaround-edit-modal-row`}>
                      <div>
                        <label>{t("time")}</label>
                      </div>
                      <div>
                        {isNullOrUndefined(editRequest.timestamp) && (
                          <button
                            className="outlined"
                            onClick={() => {
                              setEditRequest((prev) => {
                                const updatedVal = deepCopy(prev);
                                updatedVal.timestamp = mNow
                                  .toDate()
                                  .toISOString();
                                return updatedVal;
                              });
                              setHasChanges(true);
                            }}
                            disabled={isProcessing}
                          >
                            {t("select_date_time")}
                          </button>
                        )}
                        {!isNullOrUndefined(editRequest.timestamp) && (
                          <DateTimeInput
                            timeValue={editRequest.timestamp}
                            onChange={(updatedStartTime) => {
                              setEditRequest((prev) => {
                                const updatedVal = deepCopy(prev);
                                updatedVal.timestamp = updatedStartTime;
                                return updatedVal;
                              });
                              setHasChanges(true);
                            }}
                            timezone={timezone}
                          />
                        )}
                      </div>
                    </div>
                    {isBlank(editRequest.notes) && (
                      <div className={`turnaround-edit-modal-row`}>
                        <div>
                          <label>{t("photo")}</label>
                        </div>
                        <div>
                          <div className="image-input">
                            <div className="image-uploader">
                              <div>
                                <div
                                  className={`button-icon circle${
                                    isUploading ? " disabled" : ""
                                  }`}
                                  onClick={() => {
                                    if (!isUploading) {
                                      fileRef.current.click();
                                    }
                                  }}
                                >
                                  <Upload />
                                </div>
                                <input
                                  className="not-visible"
                                  type="file"
                                  ref={fileRef}
                                  accept=".png,.jpeg,.jpg"
                                  onChange={handleSelectFile}
                                />
                              </div>
                              <div>
                                <div>
                                  <ImageAsset
                                    imageUrl={item?.image?.fullSize}
                                    previewImageData={previewImageData}
                                    size={96}
                                    onClick={() => {
                                      if (!isUploading) {
                                        fileRef.current.click();
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                            <div>
                              {!isNullOrUndefined(
                                previewImageDataTimestamp
                              ) && (
                                <Tooltip
                                  title={
                                    <div className="tooltip-content">
                                      {t("use_image_time_as_timestamp")}
                                    </div>
                                  }
                                  placement="left"
                                  componentsProps={tooltipLightStyles}
                                  TransitionComponent={Fade}
                                  TransitionProps={{ timeout: 500 }}
                                  arrow
                                >
                                  <div
                                    className="image-timestamp"
                                    onClick={() => {
                                      setEditRequest((prev) => {
                                        const updatedVal = deepCopy(prev);
                                        updatedVal.timestamp =
                                          previewImageDataTimestamp;
                                        return updatedVal;
                                      });
                                      setHasChanges(true);
                                    }}
                                  >
                                    <div>
                                      <Clock />
                                      {formatLongDatetime(
                                        previewImageDataTimestamp,
                                        timezone
                                      )}
                                    </div>
                                  </div>
                                </Tooltip>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {isNullOrUndefined(editRequest.mediaUuid) &&
                      isNullOrUndefined(previewImageData) && (
                        <div className={`turnaround-edit-modal-row wide`}>
                          <div>
                            <label>{t("notes")}</label>
                          </div>
                          <div>
                            <textarea
                              rows={3}
                              value={editRequest.notes}
                              onChange={(e) => {
                                setEditRequest((prev) => {
                                  const updatedVal = deepCopy(prev);
                                  updatedVal.notes = e.target.value;
                                  return updatedVal;
                                });
                                setHasChanges(true);
                              }}
                            />
                            {isBlank(editRequest.notes) && (
                              <div className="required">
                                {t("required_no_photo_notes")}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                  </>
                )}
              </div>
            </div>
          </div>
          <div>
            <div className="modal-footer">
              <div>
                <button className="secondary" onClick={handleClose}>
                  {t("cancel")}
                </button>
              </div>
              <div>
                <button
                  onClick={handleSave}
                  disabled={!isValid || isProcessing || !hasChanges}
                >
                  {t("save")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
